import { Box, Menu, Typography } from "@mui/material";
import clsx from "clsx";
import { useState } from "react";

interface MoreCellProps {
	value?: string | any[];
	visibleCount?: number;
	valueKey?: string;
	separator?: string;
	formatter?: (value: any) => string;
}

export default function MoreCell(props: MoreCellProps) {
	const { value, visibleCount = 2, valueKey, separator = ", ", formatter } = props;

	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const open = Boolean(anchorEl);

	const getFormattedValues = () => {
		if (!value) return [];

		if (typeof value === "string") {
			return value
				.split(separator)
				.map((item) => item.trim())
				.filter(Boolean);
		}

		if (Array.isArray(value)) {
			return value.map((item) => {
				if (typeof item === "string") return item;
				if (valueKey && item?.[valueKey]) return item[valueKey];
				return String(item);
			});
		}

		return [];
	};

	const formattedValues = getFormattedValues();
	const displayValues = formatter ? formattedValues.map(formatter) : formattedValues;

	const hasMore = displayValues.length > visibleCount;
	const moreCount = hasMore ? displayValues.length - visibleCount : 0;
	const moreText = hasMore ? `${moreCount} more` : "";
	const displayText = displayValues.slice(0, visibleCount).join(separator);

	const handleClick = (event: React.MouseEvent<HTMLElement>) => {
		if (hasMore) {
			setAnchorEl(event.currentTarget);
		}
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	if (!displayValues.length) {
		return null;
	}

	return (
		<>
			<Box className="!font-sans !text-[12px] flex flex-row !items-center">
				{displayText}
				{hasMore && separator.trim()}
				&nbsp;
				{hasMore && (
					<Typography
						className={clsx("!font-sans !text-[12px]", "!text-primary !font-bold underline cursor-pointer")}
						onClick={handleClick}
					>
						{moreText}
					</Typography>
				)}
			</Box>
			<Menu
				anchorEl={anchorEl}
				open={open}
				onClose={handleClose}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "center",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "center",
				}}
				slotProps={{
					paper: {
						className: "!rounded-xl",
					},
				}}
			>
				<Box className="px-4 py-1">
					{displayValues.map((item, index) => (
						<Typography key={`${item}-${index}`} className="!font-sans-bold !text-gray-text !text-[12px] !mb-1">
							{index + 1}.&nbsp;
							<Typography className="!font-sans !text-gray-text !text-[12px]" component="span">
								{item}
							</Typography>
						</Typography>
					))}
				</Box>
			</Menu>
		</>
	);
}
